import { useNode, useEditor, Element } from "@craftjs/core";
import React, { useRef, useState, useCallback, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight, ViewModule } from "@mui/icons-material";
import { useResponsive } from "../../hooks/useResponsive";
import { selectedOutlineSx } from "../../config/settingsStyles";
import { SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";
import { ServiceCard } from "./ServiceCard";

// Component props
export interface ServicesProps {
  gap?: number;
  showNavigation?: boolean;
  cardsPerViewDesktop?: number;
  cardsPerViewTablet?: number;
  cardsPerViewMobile?: number;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

// Empty state component
const EmptyState: React.FC = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 6,
      px: 4,
      bgcolor: "#f9fafb",
      borderRadius: 2,
      border: "2px dashed #d1d5db",
      minHeight: 200,
      width: "100%",
    }}
  >
    <ViewModule sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }} />
    <Typography variant="h6" color="text.secondary" gutterBottom>
      No services yet
    </Typography>
    <Typography variant="body2" color="text.secondary" textAlign="center">
      Drag ServiceCard here to add services
    </Typography>
  </Box>
);

// Main Services container component
export const Services = ({
  gap = 24,
  showNavigation = true,
  cardsPerViewDesktop = 3,
  cardsPerViewTablet = 2,
  cardsPerViewMobile = 1,
  maxWidth = 1200,
  paddingX = 16,
  paddingTop = 16,
  paddingBottom = 16,
  children,
}: ServicesProps & { children?: React.ReactNode }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useResponsive(containerRef as React.RefObject<HTMLElement>);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  const {
    connectors: { connect, drag },
    selected,
    childNodes,
  } = useNode((state) => ({
    selected: state.events.selected,
    childNodes: state.data.nodes || [],
  }));

  const { actions, query } = useEditor();
  const childCount = childNodes.length;
  const prevChildCountRef = useRef(childCount);

  // Auto-apply style from first card to newly added cards
  useEffect(() => {
    const prevCount = prevChildCountRef.current;
    prevChildCountRef.current = childCount;

    // Only run if a new card was added (not removed)
    if (childCount <= prevCount || childCount < 2) return;

    // Get style from first card
    try {
      const firstChildId = childNodes[0];
      const firstChild = query.node(firstChildId).get();
      const firstProps = firstChild.data?.props || {};

      // Only if first card has custom style (not default)
      const hasCustomStyle = firstProps.backgroundColor && firstProps.backgroundColor !== "#ffffff";
      if (!hasCustomStyle) return;

      // Apply style to the last added card (newest one)
      const newChildId = childNodes[childCount - 1];
      const newChild = query.node(newChildId).get();
      const newProps = newChild.data?.props || {};

      // Only apply if new card has default style
      const isDefaultStyle = !newProps.backgroundColor || newProps.backgroundColor === "#ffffff";
      if (!isDefaultStyle) return;

      // Copy style from first card
      actions.setProp(newChildId, (props: any) => {
        props.backgroundColor = firstProps.backgroundColor;
        props.textColor = firstProps.textColor;
        props.borderRadius = firstProps.borderRadius;
        props.showShadow = firstProps.showShadow;
        props.padding = firstProps.padding;
      });
    } catch (e) {
      // Ignore errors
    }
  }, [childCount, childNodes, query, actions]);

  // Calculate cards per view based on screen size
  const getCardsPerView = () => {
    if (isMobile) return cardsPerViewMobile;
    if (isTablet) return cardsPerViewTablet;
    return cardsPerViewDesktop;
  };

  const cardsPerView = getCardsPerView();

  // Calculate card width — let cards fill available space naturally
  const [containerWidth, setContainerWidth] = useState(0);
  const MIN_CARD_WIDTH = 150;
  const calculatedWidth = containerWidth > 0
    ? Math.floor((containerWidth - (cardsPerView - 1) * gap - (showNavigation ? 80 : 0)) / cardsPerView)
    : 280;
  const cardWidth = Math.max(calculatedWidth, MIN_CARD_WIDTH);

  // Update container width with ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateWidth = () => {
      setContainerWidth(container.clientWidth);
      if (scrollRef.current) {
        const maxScrollValue = Math.max(0, scrollRef.current.scrollWidth - scrollRef.current.clientWidth);
        setMaxScroll(maxScrollValue);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [childCount, isMobile, isTablet]);

  // Handle scroll (scroll by cardsPerView amount)
  const handleScroll = useCallback((direction: "left" | "right") => {
    if (!scrollRef.current) return;

    const scrollAmount = (cardWidth + gap) * cardsPerView;
    const newPosition =
      direction === "left"
        ? Math.max(0, scrollPosition - scrollAmount)
        : Math.min(maxScroll, scrollPosition + scrollAmount);

    scrollRef.current.scrollTo({ left: newPosition, behavior: "smooth" });
    setScrollPosition(newPosition);
  }, [scrollPosition, maxScroll, cardWidth, gap, cardsPerView]);

  // Track scroll position
  const handleScrollUpdate = useCallback(() => {
    if (scrollRef.current) {
      setScrollPosition(scrollRef.current.scrollLeft);
      setMaxScroll(Math.max(0, scrollRef.current.scrollWidth - scrollRef.current.clientWidth));
    }
  }, []);

  const totalCardsWidth = childCount * cardWidth + (childCount - 1) * gap;
  const needsScroll = totalCardsWidth > containerWidth;
  const showArrows = showNavigation && needsScroll;
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = scrollPosition < maxScroll - 1;

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        containerRef.current = ref;
        if (ref) connect(drag(ref));
      }}
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
        mx: "auto",
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box sx={{ position: "relative" }}>
        {/* Left Arrow */}
        {showArrows && (
          <IconButton
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            sx={{
              position: "absolute",
              left: 4,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: 2,
              "&:hover": { bgcolor: "grey.100" },
              "&:disabled": { opacity: 0.3 },
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Carousel Container - This is the canvas for ServiceCard children */}
        <Box
          ref={scrollRef}
          onScroll={handleScrollUpdate}
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            gap: `${gap}px`,
            overflowX: needsScroll ? "auto" : "visible",
            scrollBehavior: "smooth",
            px: `${paddingX}px`,
            pt: `${paddingTop}px`,
            pb: `${paddingBottom}px`,
            minHeight: childCount === 0 ? 200 : undefined,
            // Center cards when fewer than cardsPerView
            justifyContent: needsScroll ? "flex-start" : "center",
            // Hide scrollbar but keep functionality
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            // Style for children
            "& > .service-card, & > *": {
              flex: `0 0 ${cardWidth}px`,
              maxWidth: `${cardWidth}px`,
            },
          }}
        >
          {childCount === 0 ? (
            <EmptyState />
          ) : (
            children
          )}
        </Box>

        {/* Right Arrow */}
        {showArrows && (
          <IconButton
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            sx={{
              position: "absolute",
              right: 4,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 10,
              bgcolor: "white",
              boxShadow: 2,
              "&:hover": { bgcolor: "grey.100" },
              "&:disabled": { opacity: 0.3 },
            }}
          >
            <ChevronRight />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

// Settings component
export const ServicesSettings = () => {
  const {
    actions: { setProp },
    gap,
    showNavigation,
    cardsPerViewDesktop,
    cardsPerViewTablet,
    cardsPerViewMobile,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    gap: node.data.props.gap,
    showNavigation: node.data.props.showNavigation,
    cardsPerViewDesktop: node.data.props.cardsPerViewDesktop,
    cardsPerViewTablet: node.data.props.cardsPerViewTablet,
    cardsPerViewMobile: node.data.props.cardsPerViewMobile,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  return (
    <SettingsSection>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Display Settings
      </Typography>

      <CheckboxField
        label="Show Navigation Arrows"
        checked={showNavigation}
        onChange={(v) => setProp((props: ServicesProps) => (props.showNavigation = v))}
      />

      <SliderField
        label="Gap"
        value={gap}
        onChange={(v) => setProp((props: ServicesProps) => (props.gap = v))}
        min={8}
        max={48}
        unit="px"
      />

      <SliderField
        label="Cards (Desktop)"
        value={cardsPerViewDesktop}
        onChange={(v) => setProp((props: ServicesProps) => (props.cardsPerViewDesktop = v))}
        min={1}
        max={6}
      />

      <SliderField
        label="Cards (Tablet)"
        value={cardsPerViewTablet}
        onChange={(v) => setProp((props: ServicesProps) => (props.cardsPerViewTablet = v))}
        min={1}
        max={4}
      />

      <SliderField
        label="Cards (Mobile)"
        value={cardsPerViewMobile}
        onChange={(v) => setProp((props: ServicesProps) => (props.cardsPerViewMobile = v))}
        min={1}
        max={2}
      />

      <SliderField
        label="Max Width"
        value={maxWidth || 1200}
        onChange={(v) => setProp((props: ServicesProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX ?? 16}
        onChange={(v) => setProp((props: ServicesProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop ?? 16}
        onChange={(v) => setProp((props: ServicesProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom ?? 16}
        onChange={(v) => setProp((props: ServicesProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />
    </SettingsSection>
  );
};

// CraftJS configuration
Services.craft = {
  displayName: "Services",
  isCanvas: true,
  props: {
    gap: 24,
    showNavigation: true,
    cardsPerViewDesktop: 3,
    cardsPerViewTablet: 2,
    cardsPerViewMobile: 1,
    maxWidth: 1200,
    paddingX: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  rules: {
    canMoveIn: (incomingNodes: any[]) => {
      return incomingNodes.every((node) => node.data.type === ServiceCard);
    },
  },
  related: {
    settings: ServicesSettings,
  },
};
